/**
 * linkLanding — what a clicked link does: the ONE owner of link kinds (Miles, 2026-10-15).
 *
 * "A link Prest0n or otherwise provides needs to default take the user to the
 * page the user clicked towards without the expanded chat interface in the
 * way." Every link to another page opens that page in a new tab, where the chat
 * starts closed, resting at the bottom right; the person's own chat mode stays
 * their default for everything else (chatModeMemory.ts keeps the two apart).
 *
 * THE KINDS (a closed list; classifyLink decides, LINK_KINDS enumerates):
 *   page      another page of this site → a new tab, the landing marker added at
 *             click time (so an href stays clean for crawlers and for copying).
 *             Another origin the site is served at (www.prest0.ai on prest0.ai)
 *             is this site: the page opens on the person's own origin, where
 *             they are signed in.
 *   external  another site → a new tab, noopener noreferrer
 *   thread    one of the person's threads (?thread=) → opened in this tab's chat:
 *             its destination is the conversation
 *   flow      a sign-in or onboarding step → this tab, where the flow continues
 *   stay      /desktop, whose live desktop belongs to this tab (Miles,
 *             2026-10-06: "the /desktop page in this tab")
 *   section   a place on this page (#…) → this tab
 *   other     a file (the in-app previewer's, floatingPreviews.ts, or the
 *             browser's), mail, phone, blobs → their own handling
 *
 * WHICH LINKS ARE PROVIDED (linkRegion). The app's own navigation is not a link
 * someone provides and keeps the in-app behaviour: anything inside a header, a
 * nav, a menu or a tab list, and on a sign-in or onboarding page a link that
 * stays in its tab (the flow's next step, its way into the app included). A
 * data-link-landing mark says it outright, "land" (the chat's transcript, both
 * footers) or "stay"; the nearest mark outranks the structure. A link aimed at
 * a named window (a connect popup) and a busy control's link are their own.
 *
 * The installed app (a home-screen window) never opens a second tab: on a phone
 * that tab is the system browser, where the person is signed out. There the page
 * opens in place, and the chat steps aside for it in this tab only.
 *
 * Lever: REACT_APP_LINK_LANDING_ACCOUNTS (workspace ids, `*` for everyone,
 * visitors included). Off when absent; deploy.yml ships it as `*`. A landing
 * marker is honoured whatever the lever says.
 *
 * Leaf: no React, no chat engine (scripts/check-chat-first-paint.mjs).
 */
import { buildEnv } from '../config/buildEnv';
import { GOOGLE_AUTHORIZED_ORIGINS } from '../config/googleOAuth.generated';
import { readTabWorkspacePin, writeTabWorkspacePin } from '../services/prest0nRuntime/tabWorkspace';
import { isStandalone } from '../utils/installState';
import { readTabMode, writeTabMode } from '../components/GlobalChatSystem/chatModeMemory';
import { fileAttachmentFromUrl, isFileName } from '../components/FilePreview/fileLinks';
import { THREAD_DEEP_LINK_PARAM, requestOpenThreadUrl } from './threadDeepLink';
import { designPreviewBase, isAppRoutePath } from '../design/previewPath';

/** The query parameter a landing tab is opened with; consumed (and removed) before the app renders. */
export const LINK_LANDING_PARAM = 'p0land';
export const LINK_LANDING_ACCOUNTS_ENV = 'REACT_APP_LINK_LANDING_ACCOUNTS';
/** The attribute that says outright whether a region's links are provided ("land") or the app's own ("stay"). */
export const LINK_LANDING_ATTRIBUTE = 'data-link-landing';
export const LINK_LANDING_LAND = 'land';
export const LINK_LANDING_STAY = 'stay';
/** Where the app's own navigation lives, read off the page's structure. */
export const APP_NAVIGATION_SELECTOR = 'header, nav, [role="navigation"], [role="menu"], [role="menubar"], [role="tablist"]';

export const LINK_KINDS = ['page', 'external', 'thread', 'flow', 'stay', 'section', 'other'] as const;
export type LinkKind = (typeof LINK_KINDS)[number];

/** Sign-in and onboarding steps: the flow continues in this tab. */
export const FLOW_PATHS: readonly string[] = ['/auth', '/start', '/signup', '/login', '/onboarding', '/accept-invite'];
/** Pages that belong to this tab. */
export const STAY_PATHS: readonly string[] = ['/desktop'];
/** The new design's demonstration prefix (src/design/previewPath.ts): a page under it is still that page. */
const DESIGN_PREFIX = '/prest0design';
/** The https origins this site is served at (config/google-oauth.json registers "every origin we serve"). */
const SITE_ORIGINS: ReadonlySet<string> = new Set(GOOGLE_AUTHORIZED_ORIGINS.filter((o) => o.startsWith('https://')));

interface Here {
  href: string;
  origin: string;
  pathname: string;
  search: string;
}

function underAny(path: string, roots: readonly string[]): boolean {
  return roots.some((r) => path === r || path.startsWith(`${r}/`));
}

function withoutDesignPrefix(path: string): string {
  return path.startsWith(`${DESIGN_PREFIX}/`) ? path.slice(DESIGN_PREFIX.length) : path;
}

/** PURE. This origin, or another origin the site is served at while this one is too. */
function onThisSite(url: URL, hereOrigin: string): boolean {
  return url.origin === hereOrigin || (SITE_ORIGINS.has(url.origin) && SITE_ORIGINS.has(hereOrigin));
}

/** This tab's workspace (the lever is account-scoped); null for a visitor or a tab not yet pinned. */
function tabAccountId(): string | null {
  return readTabWorkspacePin()?.accountId ?? null;
}

/** PURE. A target that keeps the link in its tab. */
function opensInThisTab(target: string | null | undefined): boolean {
  const t = String(target ?? '').trim().toLowerCase();
  return t === '' || t === '_self';
}

/** PURE. Is this page a sign-in or onboarding step? */
export function isFlowPage(pathname: string): boolean {
  return underAny(withoutDesignPrefix(pathname), FLOW_PATHS);
}

/** PURE. The kind of a link, from its href as written and the page it is on. */
export function classifyLink(href: string | null | undefined, here: Here): LinkKind {
  const raw = String(href ?? '').trim();
  if (!raw) return 'other';
  if (raw.startsWith('#')) return 'section';
  let url: URL;
  try {
    url = new URL(raw, here.href);
  } catch {
    return 'other';
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return 'other';
  if (fileAttachmentFromUrl(url.toString())) return 'other'; // a platform-hosted file opens in the previewer
  if (!onThisSite(url, here.origin)) return 'external';
  if (url.hash && url.pathname === here.pathname && url.search === here.search) return 'section';
  return kindOfPlace(url);
}

/** PURE. What a place on this site is: a file, a thread, a sign-in or onboarding step, /desktop, or a page. */
function kindOfPlace(url: URL): Exclude<LinkKind, 'external' | 'section'> {
  if (isFileName(url.pathname.split('/').filter(Boolean).pop() || '')) return 'other';
  if (url.searchParams.has(THREAD_DEEP_LINK_PARAM)) return 'thread';
  const path = withoutDesignPrefix(url.pathname);
  if (underAny(path, FLOW_PATHS)) return 'flow';
  if (underAny(path, STAY_PATHS)) return 'stay';
  return 'page';
}

/** PURE. The app's own front door, where the person's chosen chat mode is the welcome: the home page. */
function isHome(pathname: string): boolean {
  return pathname === '/' || pathname === DESIGN_PREFIX || pathname === `${DESIGN_PREFIX}/`;
}

/** Did this document arrive by navigating here: not a reload (the update banner's included), not back or forward? */
function arrivedByNavigating(win: Pick<Window, 'performance'>): boolean {
  try {
    const [nav] = (win.performance?.getEntriesByType?.('navigation') ?? []) as PerformanceNavigationTiming[];
    return Boolean(nav) && nav.type === 'navigate';
  } catch {
    return false;
  }
}

/** The marker's value when the tab has no workspace (a visitor); otherwise it carries the workspace's id. */
const NO_WORKSPACE = '1';
const WORKSPACE_ID = /^[A-Za-z0-9_-]{2,128}$/;

/**
 * PURE. The address a landing tab opens: the page, with the marker, on this
 * origin. The marker carries the clicking tab's workspace, so a person with two
 * workspaces open lands in the one they clicked from (the new tab's own read
 * proves membership before the pin holds the view: migrationGate.ts). Inside the
 * new design's demonstration tree (`previewBase`, src/design/previewPath.ts) an
 * app page outside it is opened inside it, as the tree's link keeper does.
 */
export function withLandingMarker(href: string, here: Pick<Here, 'href'>, previewBase = '', workspaceId: string | null = null): string {
  const hereUrl = new URL(here.href);
  let url = new URL(href, hereUrl);
  if (url.origin !== hereUrl.origin && onThisSite(url, hereUrl.origin)) url = new URL(`${url.pathname}${url.search}${url.hash}`, hereUrl);
  if (previewBase && !designPreviewBase(url.pathname) && isAppRoutePath(url.pathname)) url.pathname = `${previewBase}${url.pathname}`;
  const ws = String(workspaceId ?? '').trim();
  url.searchParams.set(LINK_LANDING_PARAM, WORKSPACE_ID.test(ws) ? ws : NO_WORKSPACE);
  return url.toString();
}

/** PURE. Is the lever on for this workspace? `*` admits everyone, a visitor included. */
export function linkLandingOn(accountId: string | null | undefined, env: Record<string, string | undefined> = buildEnv()): boolean {
  const listed = new Set(String(env[LINK_LANDING_ACCOUNTS_ENV] ?? '').split(',').map((s) => s.trim()).filter(Boolean));
  if (listed.has('*')) return true;
  const id = String(accountId ?? '').trim();
  return Boolean(id) && listed.has(id);
}

/**
 * A tab that came for a page starts with the chat closed, resting at the bottom
 * right, in this tab only: one the door opened (its marker, removed here from the
 * address so a reload, a copy or a bookmark never carries it), and any fresh tab
 * that arrived on a page however the link was followed (a click with a modifier,
 * an email, a bookmark of the page). A reload or a step back keeps the tab's own
 * mode; the home page and /desktop welcome the person in their own. A marker is
 * honoured whatever the lever says; a fresh arrival lands only while it is on.
 * Runs once, before the app renders (src/index.tsx). Returns whether the tab landed.
 */
export function consumeLinkLanding(
  win: Pick<Window, 'location' | 'history' | 'performance'> | undefined = typeof window !== 'undefined' ? window : undefined,
  deps: Pick<LinkLandingDeps, 'accountId' | 'env'> = {},
): boolean {
  if (!win) return false;
  let url: URL;
  try {
    url = new URL(win.location.href);
  } catch {
    return false;
  }
  const marker = url.searchParams.get(LINK_LANDING_PARAM);
  if (marker !== null) {
    url.searchParams.delete(LINK_LANDING_PARAM);
    try {
      win.history.replaceState(win.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {
      // the address keeps the marker; the next landing read is still this tab's own memory
    }
    // the workspace the link was clicked in, for a tab with no workspace of its own yet
    const ws = marker.trim();
    if (ws !== NO_WORKSPACE && WORKSPACE_ID.test(ws) && !readTabWorkspacePin()) writeTabWorkspacePin(ws);
  } else if (readTabMode() !== null || !arrivedByNavigating(win) || isHome(url.pathname) || kindOfPlace(url) !== 'page') {
    return false;
  } else if (!linkLandingOn((deps.accountId ?? tabAccountId)(), deps.env)) {
    return false;
  }
  writeTabMode({ open: false, fullScreen: false, spot: 'right' });
  return true;
}

/**
 * Is this link one someone provides ("land") or the app's own navigation
 * ("stay")? The nearest data-link-landing mark says so outright; else a header,
 * nav, menu or tab list is the app's navigation, and on a sign-in or onboarding
 * page a link that stays in its tab is the flow's own next step.
 */
export function linkRegion(a: Element, here: Pick<Here, 'pathname'>): 'land' | 'stay' {
  const mark = a.closest(`[${LINK_LANDING_ATTRIBUTE}]`)?.getAttribute(LINK_LANDING_ATTRIBUTE);
  if (mark === LINK_LANDING_STAY) return 'stay';
  if (mark === LINK_LANDING_LAND) return 'land';
  if (a.closest(APP_NAVIGATION_SELECTOR)) return 'stay';
  if (isFlowPage(here.pathname) && opensInThisTab(a.getAttribute('target'))) return 'stay';
  return 'land';
}

export interface LinkLandingDeps {
  open?: (url: string, target: string, features: string) => unknown;
  accountId?: () => string | null | undefined;
  installed?: () => boolean;
  openThread?: (url: string) => boolean;
  env?: Record<string, string | undefined>;
}

/**
 * Follow a link someone provided, the way its kind says. The door below calls it
 * for every plain click on a provided link; a button that follows a provided
 * address by script (a tile's or a message's "navigate" or "open_url" action)
 * calls it too, with the target it would have used. Returns true when it took
 * the link, so the caller does nothing more; false leaves the caller's own
 * behaviour (navigating in place, or the browser's).
 */
export function followProvidedLink(
  href: string,
  deps: LinkLandingDeps = {},
  opts: { here?: Here; target?: string | null } = {},
): boolean {
  if (typeof window === 'undefined') return false;
  const here = opts.here ?? window.location;
  const thisTab = opensInThisTab(opts.target);
  if (!thisTab && String(opts.target).trim().toLowerCase() !== '_blank') return false; // a named window is the caller's own
  const kind = classifyLink(href, here);
  if (kind !== 'page' && kind !== 'external' && kind !== 'thread') return false;
  const workspaceId = (deps.accountId ?? tabAccountId)() ?? null;
  if (!linkLandingOn(workspaceId, deps.env)) return false;
  if (kind === 'thread') return thisTab && (deps.openThread ?? requestOpenThreadUrl)(new URL(href, here.href).toString());
  const open = deps.open ?? ((url: string, target: string, features: string) => window.open(url, target, features));
  if (kind === 'external') {
    if (!thisTab) return false; // it already opens in a new tab
    open(new URL(href, here.href).toString(), '_blank', 'noopener,noreferrer');
    return true;
  }
  // the installed app: the page opens in place (the app's own navigation), where the chat steps
  // aside for it in this tab only (chatModeMemory: a presentation, never the person's default)
  if ((deps.installed ?? isStandalone)()) return false;
  open(withLandingMarker(href, here, designPreviewBase(here.pathname), workspaceId), '_blank', 'noopener');
  return true;
}

/**
 * The one door every click on a link passes: a capture listener on the document,
 * so it decides before any component's own handler (a router link, the chat's
 * transcript) can navigate in place. Only a plain click on a provided link is
 * taken; a click with a modifier, a middle click, a download, a busy control and
 * the app's own navigation keep the browser's or the app's own behaviour.
 * Returns the uninstaller.
 */
export function installLinkLanding(doc: Document = document, deps: LinkLandingDeps = {}): () => void {
  const onClick = (e: MouseEvent): void => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const target = e.target as Element | null;
    const a = target && typeof target.closest === 'function' ? (target.closest('a[href]') as HTMLAnchorElement | null) : null;
    if (!a || a.hasAttribute('download') || a.getAttribute('aria-busy') === 'true') return;
    const here = doc.defaultView?.location ?? window.location;
    if (linkRegion(a, here) === 'stay') return;
    if (followProvidedLink(a.getAttribute('href') || '', deps, { here, target: a.getAttribute('target') })) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  doc.addEventListener('click', onClick, true);
  return () => doc.removeEventListener('click', onClick, true);
}
